import { ChartBar, ChartRangeType, DropdownWithStatistic } from "components";
import { Loader, Stat } from "ui";

import { RevenueBreakdownResponse } from "../../../apis";
import { CategoryBreakdown, ChannelBreakdown, SectionCard } from "../atoms";
import { formatXof, getTotalsMetricDelta } from "../financialFormat";

import styles from "./RevenueSection.module.scss";

interface RevenueSectionProps {
  data: RevenueBreakdownResponse | undefined;
  isLoading: boolean;
  showComparison: boolean;
  range: [Date, Date];
  activeRangeType: ChartRangeType;
}

const RevenueSection = ({
  data,
  isLoading,
  showComparison,
  range,
  activeRangeType
}: RevenueSectionProps) => {
  if (!data) {
    return (
      <SectionCard title="Revenue">
        <Loader />
      </SectionCard>
    );
  }

  const delta = getTotalsMetricDelta(
    data.totals,
    data.previousTotals,
    data.deltas,
    "netRevenue"
  );

  return (
    <SectionCard title="Revenue">
      <div className={styles.topBlock}>
        <Stat
          size="lg"
          title="Total revenue"
          amount={formatXof(data.totals.netRevenue)}
          percentage={showComparison ? (delta ?? undefined) : undefined}
        />

        <div className={styles.chartWrap}>
          <ChartBar
            datepicker={null}
            activeRangeType={activeRangeType}
            onRangeTypeChange={() => {}}
            dateRange={range}
            isLoading={isLoading}
            data={data.series.map((point) => ({
              date: point.date,
              value: point.netRevenue
            }))}
          />
        </div>
      </div>

      <DropdownWithStatistic label="Revenue by sales channel" defaultOpen>
        <ChannelBreakdown
          channels={data.byChannel}
          topResellers={data.byReseller}
          totalValue={data.totals.netRevenue}
        />
      </DropdownWithStatistic>

      <DropdownWithStatistic label="Revenue by category" defaultOpen>
        <CategoryBreakdown
          level1={data.byLevel1}
          level2={data.byLevel2}
          variants={data.byVariant}
          metric="netRevenue"
          totalValue={data.totals.netRevenue}
          showComparison={showComparison}
          shareLabel="of period revenue"
          productsLabel="Products sold"
        />
      </DropdownWithStatistic>
    </SectionCard>
  );
};

export default RevenueSection;
